import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { Colors } from '../../common/Colors';
import { Variables } from '../../common/Variables';
import { media } from '../../styles/MediaQueries';
import { helperTypographyText } from '../../helpers';
import { getRem } from '../../utils';
import { CLOSE_TIMEOUT_MS, SizePropType } from '.';

const sizeList: { [key in SizePropType]: number } = {
  small: 430,
  medium: 680,
  big: 912
};

export const className = {
  overlay: css`
    overflow: auto;
    -webkit-overflow-scrolling: touch;
    position: fixed;
    z-index: 1000;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    padding: 0 2rem;
    text-align: center;
    background-color: ${Colors.ut.modalOverlay};
    backdrop-filter: blur(2rem);
    transition: opacity 0.3s;
    opacity: 0;

    &:before {
      content: '';
      display: inline-block;
      height: 100%;
      vertical-align: middle;
      width: 0;
      font-size: 0;

      @media ${media.phonablet} {
        display: none;
      }
    }

    @media ${media.phonablet} {
      padding: 0;
    }
  `,
  overlayAfterOpen: css`
    && {
      opacity: 1;
    }
  `,
  overlayBeforeClose: css`
    && {
      opacity: 0;
      transition-duration: ${`${CLOSE_TIMEOUT_MS}ms`};
    }
  `,

  content: (size: SizePropType) => css`
    outline: 0;
    display: inline-flex;
    flex-direction: column;
    vertical-align: middle;
    margin: 2rem 0;
    max-width: ${getRem(typeof size === 'number' ? size : sizeList[size])};
    width: 100%;
    min-height: 55rem;
    text-align: left;
    border-radius: ${Variables.popupRadius};
    box-shadow: ${Variables.popupShadow};

    @media ${media.tablesktop} {
      transition: transform 0.6s 0.1s cubic-bezier(0.18, 0.83, 0.34, 1);
      transform: translateY(-100%);
    }

    @media ${media.phonablet} {
      margin: 0;
      max-width: none;
      min-height: auto;
      height: 100%;
      border-radius: 0;
      box-shadow: none;
    }
  `,
  contentAfterOpen: css`
    @media ${media.tablesktop} {
      && {
        transform: translateY(0);
      }
    }
  `,
  contentBeforeClose: css`
    @media ${media.tablesktop} {
      && {
        transform: scale(0.8);
        transition-delay: 0s;
        transition-duration: ${`${CLOSE_TIMEOUT_MS}ms`};
        transition-timing-function: ease;
      }
    }
  `
};

export const Header = styled('div')`
  padding: 4.8rem 5rem 2rem;

  @media ${media.phonablet} {
    padding: 1.5rem 1.6rem 1rem;
  }
`;

interface ContainerProps {
  isClosable?: boolean;
}

export const Container = styled('div')<ContainerProps>`
  position: relative;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  background-color: ${Colors.ut.bgUp};
  border-radius: ${Variables.popupRadius};

  @media ${media.phonablet} {
    border-radius: 0;
    ${Header} {
      margin-right: ${props => props.isClosable && '4rem'};
    }
    padding-bottom: env(
      safe-area-inset-bottom,
      0
    ); /*стиль для длинных айфонов, заработает только вместе с правильным вьюпортом*/
  }
`;

export const Preheader = styled('div')`
  ${helperTypographyText({
    size: 'small',
    lh: 'big',
    caps: true
  })};

  margin-bottom: -1rem;
  padding: 0.6rem 5rem 1.4rem;
  color: ${Colors.text.inverse};
  letter-spacing: 3px;
  text-align: center;
  background-color: var(--preheader-fallback-bg, #82878d);
  border-radius: ${Variables.popupRadius} ${Variables.popupRadius} 0 0;

  @media ${media.phonablet} {
    padding-left: 1.6rem;
    padding-right: 1.6rem;
    border-radius: 0;
  }

  @supports (backdrop-filter: blur(20px)) or (-webkit-backdrop-filter: blur(20px)) {
    background-color: var(--preheader-bg, rgba(255, 255, 255, 0.1));
  }
`;

export const Title = styled('div')`
  padding: 4.4rem 5rem 2rem;

  @media ${media.phonablet} {
    padding: 1.5rem 1.6rem 1rem;
  }
`;

export const buttonClose = css`
  position: absolute;
  top: 1.1rem;
  right: 1rem;

  @media ${media.phonablet} {
    top: 1.2rem;
  }
`;

export const Content = styled('div')`
  flex-grow: 1;
  overflow: auto;
  padding: 0 5rem;

  @media ${media.phonablet} {
    padding: 0 1.6rem;
  }
`;

export const Form = styled('form')`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  min-height: 0;
`;

export const Footer = styled('div')`
  margin-top: auto;
  padding: 3rem 5rem 5rem;

  @media ${media.phonablet} {
    padding: 1.2rem 1.6rem;
  }
`;

export const CentredContent = styled('div')`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
`;
